import { useCallback, useEffect, useId } from 'react';
import styled from '@emotion/styled';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { NICKNAME_MAX_LENGTH } from 'src/components/AzarOnly/Login/constants';
import SkipButton from 'src/components/AzarOnly/Login/SkipButton';
import { Description } from 'src/components/AzarOnly/Login/Steps/styles';
import useRegister from 'src/components/AzarOnly/Login/useRegister';
import Button from 'src/components/Button';
import Input from 'src/components/Input';
import { ModalContent, ModalFooter as ModalFooterBase, ModalHeader as ModalHeaderBase, ModalTitle, ModalTitleContainer, ModalTitleDescription } from 'src/components/ModalLayout';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
import { loginModalHeaderAtom } from 'src/stores/phoneNumberLogin/atoms';
import { FALLBACK_SIGNUP_SETTINGS, nicknameAtom, signUpSettingsLoadableAtom } from 'src/stores/register/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
const FormWrap = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding-top: 32px;
`;
const InputCount = styled.p`
  text-align: end;
  color: ${({
  theme
}) => theme.color.content9};
  font-size: 13px;
`;
const CurrentCount = styled.span<{
  isValid: boolean;
}>`
  color: ${({
  isValid,
  theme
}) => isValid ? theme.color.content9 : theme.color.accentA1};
`;
const DesktopSkipButton = styled(SkipButton)`
  ${({
  theme
}) => theme.screenSize.mobile} {
    display: none;
  }
`;
const MobileSkipButton = styled(SkipButton)`
  display: none;

  ${({
  theme
}) => theme.screenSize.mobile} {
    display: block;
    width: fit-content;
    padding: 0;
  }
`;
const ModalHeader = styled(ModalHeaderBase)`
  flex-direction: row;
  justify-content: flex-end;
`;
const ModalFooter = styled(ModalFooterBase)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;
type Props = {
  defaultName?: string;
};
const NicknameStep = ({
  defaultName = ''
}: Props) => {
  const {
    tryRegister
  } = useRegister();
  const {
    t
  } = useTranslation();
  const [nicknameInRegisterForm, setNicknameInRegisterForm] = useAtom(nicknameAtom);
  const nickname = 'nickname' in nicknameInRegisterForm ? nicknameInRegisterForm.nickname : defaultName;
  const device = useCssMediaDevice();
  const formId = useId();
  const setLoginModalHeader = useSetAtom(loginModalHeaderAtom);
  const signUpSettingsLoadable = useAtomValue(signUpSettingsLoadableAtom);
  const signUpSettings = signUpSettingsLoadable.state === 'hasData' ? signUpSettingsLoadable.data : FALLBACK_SIGNUP_SETTINGS;
  const showSkipButton = signUpSettings.nicknameSettings?.showNicknameSkipButton === true;
  const registerWithNickname = useCallback(async () => {
    setNicknameInRegisterForm({
      status: 'selected',
      nickname
    });
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__CLICK_NEXT_NAME,
      eventParams: {
        action_category: 'click',
        tab: 'signup',
        page: 'name',
        target: 'next',
        // 기존 nickname이 first name과 last name으로 분리되면서, nickname은 first name이 됨
        // 현재 Web Client에서는 last name을 입력받지 않기 때문에 null로 전달
        first_name: nickname,
        last_name: null,
        is_prefilled: !!defaultName,
        is_reused_name: defaultName === nickname
      }
    });
    await tryRegister();
  }, [setNicknameInRegisterForm, nickname, defaultName, tryRegister]);
  const handleSkip = useCallback(async () => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__CLICK_SKIP_NAME,
      eventParams: {
        action_category: 'click',
        tab: 'signup',
        page: 'name',
        target: 'skip'
      }
    });
    setNicknameInRegisterForm({
      status: 'skipped'
    });
    await tryRegister();
  }, [tryRegister, setNicknameInRegisterForm]);
  const handleInput = (text: string) => {
    setNicknameInRegisterForm({
      status: 'draft',
      nickname: text
    });
  };
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__IMPRESSION_NAME,
      eventParams: {
        action_category: 'impression',
        tab: 'signup',
        page: 'name',
        target: 'name',
        is_prefilled: !!defaultName
      }
    });
  }, [defaultName]);
  useEffect(() => {
    if (nicknameInRegisterForm.status === 'selected') {
      setNicknameInRegisterForm({
        status: 'draft',
        nickname: nicknameInRegisterForm.nickname
      });
    }
  }, []);
  useEffect(() => {
    if (showSkipButton) {
      setLoginModalHeader({
        component: <ModalHeader>
            <MobileSkipButton onSkip={handleSkip} data-testid='NicknameStep-button-skip-mobile' />
          </ModalHeader>
      });
    }
    return () => {
      setLoginModalHeader(undefined);
    };
  }, [handleSkip, setLoginModalHeader, showSkipButton]);
  const canRegisterNickname = nickname && nickname.length <= NICKNAME_MAX_LENGTH;
  return <>
      <ModalTitleContainer data-sentry-element="ModalTitleContainer" data-sentry-source-file="NicknameStep.tsx">
        <ModalTitle data-testid='LoginModal-NicknameStep' data-sentry-element="ModalTitle" data-sentry-source-file="NicknameStep.tsx">
          {t('signup__info_name_title')}
        </ModalTitle>
        <ModalTitleDescription data-sentry-element="ModalTitleDescription" data-sentry-source-file="NicknameStep.tsx">
          <Description data-sentry-element="Description" data-sentry-source-file="NicknameStep.tsx">{t('signup__info_name_desc')}</Description>
        </ModalTitleDescription>
      </ModalTitleContainer>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="NicknameStep.tsx">
        <FormWrap id={formId} onSubmit={e => {
        e.preventDefault();
        if (canRegisterNickname) {
          registerWithNickname();
        }
      }} data-sentry-element="FormWrap" data-sentry-source-file="NicknameStep.tsx">
          <Input type='text' value={nickname} maxLength={NICKNAME_MAX_LENGTH} onChangeText={handleInput} label={t('signup__info_name_guide')} placeholder={t('signup__info_name_placeholder')} autoFocus={device !== 'mobile'} defaultValue={nickname} data-testid='LoginModal-NicknameStep-input' data-sentry-element="Input" data-sentry-source-file="NicknameStep.tsx" />
          <InputCount data-sentry-element="InputCount" data-sentry-source-file="NicknameStep.tsx">
            <CurrentCount isValid={(nickname?.length ?? 0) <= NICKNAME_MAX_LENGTH} data-testid='LoginModal-NicknameStep-input-length' data-sentry-element="CurrentCount" data-sentry-source-file="NicknameStep.tsx">
              {nickname?.length ?? 0}
            </CurrentCount>
            /{NICKNAME_MAX_LENGTH}
          </InputCount>
        </FormWrap>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="NicknameStep.tsx">
        <Button id='sign-up-nickname-next' form={formId} type='submit' disabled={!canRegisterNickname} data-testid='LoginModal-NicknameStep-next' data-sentry-element="Button" data-sentry-source-file="NicknameStep.tsx">
          {t('SIGNUP__NEXT')}
        </Button>
        {showSkipButton && <DesktopSkipButton onSkip={handleSkip} data-testid='NicknameStep-button-skip' />}
      </ModalFooter>
    </>;
};
export default NicknameStep;